export interface AudioMetadata {
  title?: string;
  artist?: string;
  album?: string;
  year?: number;
  duration?: number;
  genre?: string[];
}

/**
 * Extract audio metadata from a File object by sending to server
 *
 * Uses server-side API route (/api/audio/metadata) to extract metadata
 * with the full music-metadata library (which has Node.js dependencies).
 *
 * Falls back to empty metadata if server extraction fails.
 */
export async function extractAudioMetadata(file: File): Promise<AudioMetadata> {
  try {
    // Send file to server for metadata extraction
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/api/audio/metadata', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const metadata = await response.json();
    return metadata;
  } catch (error) {
    console.error('Failed to extract metadata from server:', error);
    // Return empty metadata and let filename parsing handle it
    return {};
  }
}

export function buildSpotifySearchQuery(metadata: AudioMetadata, filename?: string): string {
  const parts: string[] = [];

  // Prioritize title and artist from metadata
  if (metadata.title) {
    parts.push(metadata.title);
  }

  if (metadata.artist) {
    parts.push(metadata.artist);
  }

  // If no metadata, try to parse filename
  if (parts.length === 0 && filename) {
    // Remove file extension
    const nameWithoutExt = filename.replace(/\.(mp3|wav|ogg|m4a|flac)$/i, '');

    // Try to parse common patterns like "Artist - Title" or "Title - Artist"
    const hyphenSplit = nameWithoutExt.split(' - ');
    if (hyphenSplit.length === 2) {
      parts.push(hyphenSplit[1], hyphenSplit[0]);
    } else {
      // Replace underscores/dots with spaces and use as-is
      parts.push(nameWithoutExt.replace(/[_\.]/g, ' '));
    }
  }

  return parts.join(' ').trim();
}

const GENERIC_QUERIES = new Set([
  'track',
  'audio',
  'recording',
  'untitled',
  'unknown',
  'song',
  'file',
  'download',
  'music',
  'audio file',
  'new recording',
  'voice memo',
  'voice recording',
  'audio recording',
]);

/** True when a query is worth sending to Spotify (not "Track 01" / empty / digits). */
export function isUsefulSpotifyQuery(query: string): boolean {
  const normalized = query.trim().replace(/\s+/g, ' ');
  if (normalized.length < 4) return false;
  const letters = normalized.replace(/[^A-Za-z]/g, '');
  if (letters.length < 3) return false;
  if (GENERIC_QUERIES.has(normalized.toLowerCase())) return false;
  if (/^(track|audio|recording|untitled|song|file|music)\s*\d+$/i.test(normalized)) {
    return false;
  }
  return true;
}
